import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type UseQueryResult } from '@/lib/query';
import { CalendarCheck } from 'lucide-react';
import { useOrganizationBookings } from '@/modules/bookings/hooks/useOrganizationBookings';
import { DataTableShell } from '@/shared/ui/data-table-shell';
import { SortHeader } from '@/shared/ui/SortHeader';
import { BookingStatusBadge } from '@/modules/bookings/components/StatusBadge';
import { DriverProfileDialog } from '@/modules/bookings/components/DriverProfileDialog';
import { CompanyBookingStatus, CompanyBookingDto, DriverGroupDto } from '@cargovibe/shared-types';
import { QueryErrorMessage } from '@/shared/ui/QueryErrorMessage';
import { useDriverGroups } from '@/modules/driver-groups';
import { BookingsFilterBar } from '@/modules/bookings/components/BookingsFilterBar';

type SortKey =
  'internalBookingId' | 'userName' | 'arrivalTime' | 'endTime' | 'status' | 'grossPrice';

type SortDir = 'asc' | 'desc';

function parseUtcTimestamp(isoUtc: string): Date {
  return new Date(isoUtc.endsWith('Z') ? isoUtc : `${isoUtc}Z`);
}

function formatDateTime(isoUtc: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parseUtcTimestamp(isoUtc));
}

function formatPrice(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

function compareBookings(
  bookingA: CompanyBookingDto,
  bookingB: CompanyBookingDto,
  key: SortKey
): number {
  if (key === 'arrivalTime' || key === 'endTime') {
    return parseUtcTimestamp(bookingA[key]).getTime() - parseUtcTimestamp(bookingB[key]).getTime();
  }

  const aValue: string | number = bookingA[key];
  const bValue: string | number = bookingB[key];
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return aValue - bValue;
  }
  return String(aValue).localeCompare(String(bValue));
}

export default function BookingsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilters, setStatusFilters] = useState<Set<CompanyBookingStatus>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey | null>('arrivalTime');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [activeDriverId, setActiveDriverId] = useState<string | null>(null);
  const [activeDriverName, setActiveDriverName] = useState<string | undefined>(undefined);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

  const {
    data: bookings,
    isLoading,
    isFetching,
    error,
    refetch,
  }: UseQueryResult<CompanyBookingDto[]> = useOrganizationBookings();

  const { data: groups }: UseQueryResult<DriverGroupDto[]> = useDriverGroups();

  const handleSort = (key: SortKey): void => {
    if (sortKey === key) {
      setSortDir((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleStatus = (status: CompanyBookingStatus): void => {
    setStatusFilters((previousFilters) => {
      const next: Set<CompanyBookingStatus> = new Set(previousFilters);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const clearFilters = (): void => {
    setStatusFilters(new Set());
    setActiveGroupId(null);
  };

  const handleSelectDriver = (booking: CompanyBookingDto) => (): void => {
    setActiveDriverId(booking.userId);
    setActiveDriverName(booking.userName);
  };

  const closeDriverProfile = (): void => {
    setActiveDriverId(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = (): void => {
    void refetch();
  };

  const activeGroup: DriverGroupDto | undefined = groups?.find(
    (group: DriverGroupDto): boolean => group.id === activeGroupId
  );

  const visibleBookings: CompanyBookingDto[] = useMemo<CompanyBookingDto[]>(() => {
    if (!bookings) return [];
    const lowerSearchTerm: string = searchTerm.toLowerCase();
    const memberIds: Set<string> | null = activeGroup ? new Set(activeGroup.memberIds) : null;
    const filtered: CompanyBookingDto[] = bookings.filter((booking) => {
      if (lowerSearchTerm) {
        const matchesSearch: boolean =
          booking.internalBookingId.toLowerCase().includes(lowerSearchTerm) ||
          booking.userName.toLowerCase().includes(lowerSearchTerm);
        if (!matchesSearch) return false;
      }
      if (memberIds && !memberIds.has(booking.userId)) return false;
      return !(statusFilters.size > 0 && !statusFilters.has(booking.status));
    });

    if (!sortKey) return filtered;
    const directionMultiplier: 1 | -1 = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort(
      (bookingA, bookingB) => compareBookings(bookingA, bookingB, sortKey) * directionMultiplier
    );
  }, [bookings, searchTerm, statusFilters, activeGroup, sortKey, sortDir]);

  if (error) return <QueryErrorMessage error={error} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{t('bookings.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('bookings.subtitle')}</p>
      </div>

      <BookingsFilterBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        statusFilters={statusFilters}
        onToggleStatus={toggleStatus}
        groups={groups ?? []}
        activeGroupId={activeGroupId}
        onSelectGroup={setActiveGroupId}
        onClearFilters={clearFilters}
        isFetching={isFetching}
        onRefresh={handleRefresh}
      />

      <DataTableShell
        icon={CalendarCheck}
        count={visibleBookings.length}
        countLabel={t('bookings.bookingLabel')}
        isLoading={isLoading}
        isEmpty={visibleBookings.length === 0}
      >
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-muted-foreground bg-muted/50 border-b border-border">
            <tr>
              <SortHeader
                label={t('bookings.bookingId')}
                sortKey="internalBookingId"
                active={sortKey}
                direction={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                label={t('bookings.driver')}
                sortKey="userName"
                active={sortKey}
                direction={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                label={t('bookings.arrival')}
                sortKey="arrivalTime"
                active={sortKey}
                direction={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                label={t('bookings.departure')}
                sortKey="endTime"
                active={sortKey}
                direction={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                label={t('bookings.statusLabel')}
                sortKey="status"
                active={sortKey}
                direction={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                label={t('bookings.price')}
                sortKey="grossPrice"
                active={sortKey}
                direction={sortDir}
                onSort={handleSort}
                align="right"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visibleBookings.map((booking) => (
              <tr key={booking.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4 font-mono text-xs text-foreground">
                  {booking.internalBookingId}
                </td>
                <td className="px-6 py-4 font-medium">
                  <button
                    type="button"
                    onClick={handleSelectDriver(booking)}
                    className="text-primary hover:underline"
                  >
                    {booking.userName}
                  </button>
                </td>
                <td className="px-6 py-4 text-muted-foreground">
                  {formatDateTime(booking.arrivalTime, i18n.language)}
                </td>
                <td className="px-6 py-4 text-muted-foreground">
                  {formatDateTime(booking.endTime, i18n.language)}
                </td>
                <td className="px-6 py-4">
                  <BookingStatusBadge status={booking.status} />
                </td>
                <td className="px-6 py-4 text-right font-medium text-foreground">
                  {formatPrice(booking.grossPrice, i18n.language)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTableShell>

      <DriverProfileDialog
        driverId={activeDriverId}
        fallbackName={activeDriverName}
        onClose={closeDriverProfile}
      />
    </div>
  );
}
